import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import Title from '../../components/common/Title';
import { apiRequest } from '../../utils';
import './Finance.css';

/* Everything captured, three ways of looking at it.

   'unpaid' leads because it is the only view that implies an action — these are
   invoices somebody still owes money on. 'new' is the pile nobody has confirmed
   yet, which is what a bad camera day leaves behind. */
const VIEWS = ['unpaid', 'new', 'all'];

const money = (v) => (v == null ? '' : Number(v).toLocaleString('nl-BE',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const FinanceDocuments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [view, setView] = useState('unpaid');
  const [query, setQuery] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (query.trim()) params.set('q', query.trim());
      const data = await apiRequest('GET', `/finance/documents?${params}`);
      setDocuments(data.documents || []);
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [view, query]);

  // Typing on a phone: wait for the pause, not for every letter.
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <Base activeNav="home">
      <Title my="3">{t('financeCaptured')}</Title>

      <Card>
        <div className="fin-tabs">
          {VIEWS.map((v) => (
            <button key={v} type="button"
                    className={`fin-tab ${view === v ? 'fin-tab--on' : ''}`}
                    onClick={() => setView(v)}>
              {t(`financeView_${v}`)}
            </button>
          ))}
        </div>
        <input className="fin-input" value={query} autoComplete="off"
               placeholder={t('financeSearchSupplier')}
               onChange={(e) => setQuery(e.target.value)} />
      </Card>

      <Card>
        {loading ? <Loading /> : (documents.length ? documents.map((d) => (
          <div key={d.id} className="fin-doc" onClick={() => navigate(`/finance/${d.id}`)}>
            <div className="fin-doc__main">
              <span className="fin-doc__name">{d.supplier_name || t('financeUnknownSupplier')}</span>
              <span className="fin-doc__sub">
                {[d.document_date?.slice(0, 10), d.document_number, d.category_name]
                  .filter(Boolean).join(' · ')}
              </span>
            </div>
            <div className="fin-doc__side">
              <span className="fin-doc__amount">{money(d.amount_gross)}</span>
              {d.open != null && d.open > 0.01 && (
                <span className="fin-doc__flag">{t('financeOpen', { amount: money(d.open) })}</span>
              )}
              {!d.has_photo && <span className="fin-doc__flag">{t('financeNoPhoto')}</span>}
            </div>
            <Icon name="angle-right" />
          </div>
        )) : <p className="fin-hint">{t('financeNothingHere')}</p>)}
      </Card>
    </Base>
  );
};

export default FinanceDocuments;
